// src/components/Airport/EditAirportDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  createAirport,
  updateAirport,
  Airport,
  CreateAirportRequest,
  UpdateAirportRequest,
  AirportApiError,
} from '../../services/airportApi';
import { listUsers, User } from '../../services/userApi';
import { hasRole } from '../../services/authApi';

interface EditAirportDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  airport: Airport | null; // null for create, Airport for edit
}

// Common timezones
const TIMEZONES = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'America/New_York', label: 'America/New_York (Eastern Time)' },
  { value: 'America/Chicago', label: 'America/Chicago (Central Time)' },
  { value: 'America/Denver', label: 'America/Denver (Mountain Time)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (Pacific Time)' },
  { value: 'America/Phoenix', label: 'America/Phoenix (Mountain Time - No DST)' },
  { value: 'America/Anchorage', label: 'America/Anchorage (Alaska Time)' },
  { value: 'Pacific/Honolulu', label: 'Pacific/Honolulu (Hawaii Time)' },
  { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai (GST)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST/AEDT)' },
];


const EditAirportDialog: React.FC<EditAirportDialogProps> = ({
  open,
  onClose,
  onSuccess,
  airport,
}) => {
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState<string>('');
  const [selectedManagers, setSelectedManagers] = useState<User[]>([]);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const isEditMode = airport !== null;

  // Load available users for managers dropdown
  useEffect(() => {
    if (open) {
      loadUsers();
    }
  }, [open]);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await listUsers({ page: 1, pageSize: 1000, isActive: true });
      // Filter to only show users with Airport Manager role
      // Convert userApi.User to authApi.User format for hasRole check
      const airportManagers = response.users.filter(user => {
        const authUser = {
          userId: user.userId,
          email: user.email,
          displayName: user.displayName || user.email,
          roles: (user.roles || []).map(r => r as any) as any[],
          isActive: user.isActive,
          lastLoginAt: user.lastLoginAt || null,
        };
        return hasRole(authUser as any, 'Airport Manager');
      });
      setAvailableUsers(airportManagers);
    } catch (err) {
      console.error('Error loading users:', err);
      // Don't show error to user, just log it
    } finally {
      setLoadingUsers(false);
    }
  };

  // Helper function to parse error messages and replace user IDs with user names
  const parseErrorMessage = (message: string): string => {
    // Pattern: "User {id} must have..." or "User {id}..." 
    const userIdPattern = /User\s+(\d+)/g;
    let parsedMessage = message;
    
    // Find all user ID references in the error message
    const matches = Array.from(message.matchAll(userIdPattern));
    
    matches.forEach(match => {
      const userId = parseInt(match[1], 10);
      const user = availableUsers.find(u => u.userId === userId);
      
      if (user) {
        // Replace "User {id}" with "User {displayName} ({email})"
        const userDisplay = `${user.displayName || user.email} (${user.email})`;
        parsedMessage = parsedMessage.replace(match[0], userDisplay);
      } else {
        // If user not found, at least replace with a more friendly format
        parsedMessage = parsedMessage.replace(match[0], `User ID ${userId}`);
      }
    });
    
    return parsedMessage;
  };

  // Initialize form with airport data
  useEffect(() => {
    if (open) {
      if (airport) {
        // Edit mode
        setName(airport.name || '');
        setTimezone(airport.timezone || '');
        // Set selected managers - we need to match by userId
        if (airport.managers && airport.managers.length > 0) {
          const managerUsers = airport.managers.map(manager => {
            // Find user in availableUsers or create a placeholder
            const user = availableUsers.find(u => u.userId === manager.userId);
            if (user) {
              return user;
            }
            // If user not found yet, create a placeholder (will be updated when users load)
            return {
              userId: manager.userId,
              email: manager.email,
              displayName: manager.displayName,
              isActive: true,
              role: null,
            } as User;
          });
          setSelectedManagers(managerUsers);
        } else {
          setSelectedManagers([]);
        }
      } else {
        // Create mode
        setName('');
        setTimezone('UTC');
        setSelectedManagers([]);
      }
      setError(null);
      setValidationErrors({});
    }
  }, [airport, open, availableUsers]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Name validation
    if (!name.trim()) {
      errors.name = 'Airport name is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const managerIds = selectedManagers.map(m => m.userId);

      if (isEditMode && airport) {
        // Update existing airport
        // IMPORTANT: managers field is REQUIRED and must always be an array
        const updateData: UpdateAirportRequest = {
          name: name.trim(),
          timezone: timezone || null,
          managers: managerIds, // Always include as array (can be empty [])
        };

        await updateAirport(airport.airportId, updateData);
      } else {
        // Create new airport
        // IMPORTANT: managers field is REQUIRED and must always be an array
        const createData: CreateAirportRequest = {
          name: name.trim(),
          timezone: timezone || null,
          managers: managerIds, // Always include as array (can be empty [])
        };

        await createAirport(createData);
      }
      
      onSuccess();
    } catch (err) {
      const apiError = err as AirportApiError;
      console.error('EditAirportDialog: Error saving airport:', err);
      
      // Provide more helpful error messages
      let errorMessage = apiError.message || 'Failed to save airport';
      
      if (apiError.code === 'NETWORK_ERROR') {
        errorMessage = apiError.message || 'Network error: Unable to connect to the backend server.';
      } else if (apiError.code === 'NOT_FOUND') {
        errorMessage = `Airport not found. The backend endpoint may not be implemented yet.`;
      } else if (apiError.code === 'AUTH_REQUIRED') {
        errorMessage = 'Authentication required. Please refresh the page and try again.';
      } else if (apiError.code === 'VALIDATION_ERROR') {
        // Parse error message to replace user IDs with user names
        errorMessage = parseErrorMessage(apiError.message || 'Validation failed. Please check your input.');
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setError(null);
      setValidationErrors({});
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit Airport' : 'Create Airport'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TextField
            label="Airport Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setValidationErrors({ ...validationErrors, name: '' });
            }}
            required
            fullWidth
            autoFocus
            disabled={isSubmitting}
            error={!!validationErrors.name}
            helperText={validationErrors.name || 'Name of the airport (e.g., JFK, LAX)'}
          />

          <FormControl fullWidth disabled={isSubmitting}>
            <InputLabel id="timezone-select-label" shrink={true}>
              Timezone
            </InputLabel>
            <Select
              labelId="timezone-select-label"
              id="timezone-select"
              value={timezone}
              label="Timezone"
              onChange={(e) => setTimezone(e.target.value)}
              displayEmpty
              renderValue={(selected: string) => {
                if (!selected) {
                  return <span style={{ color: '#999', fontStyle: 'italic' }}>Select timezone (optional)</span>;
                }
                return TIMEZONES.find((t) => t.value === selected)?.label || selected;
              }}
            >
              <MenuItem value="">
                <em>UTC (default)</em>
              </MenuItem>
              {TIMEZONES.map((tz) => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Select the timezone for this airport (optional, defaults to UTC)</FormHelperText>
          </FormControl>

          <Autocomplete
            multiple
            options={availableUsers}
            getOptionLabel={(option) => `${option.displayName || option.email} (${option.email})`}
            value={selectedManagers}
            onChange={(_, newValue) => setSelectedManagers(newValue)}
            loading={loadingUsers}
            disabled={isSubmitting}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Managers"
                helperText="Select users who will manage this airport (optional)"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.userId}
                  label={option.displayName || option.email}
                />
              ))
            }
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || !name.trim()}
        >
          {isSubmitting ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Airport' : 'Create Airport')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAirportDialog;

